import { For, Show } from "solid-js";
import type { Flow, Flows } from "../overview.ts";
import { formatNumber } from "../format.ts";

const COLORS: Record<Flow["kind"], string> = {
  grid: "var(--m-el)",
  pv: "var(--m-pv)",
  load: "var(--m-el)",
  export: "var(--ok)",
  rest: "var(--hatch)",
  unmeasured: "var(--line-2)",
};

const W = 640;
const TOP = 26;
const GAP = 8;
const FS = 11;
const XL = 160;
const XR = W - 170;
const CX = W / 2 - 7;

/**
 * Flows into and out of one node (prototype "Bilans teraz"): band widths are to scale, inputs on the left, outlets on
 * the right. The part no point measures is hatched. Clicking an outlet that is a point calls `onPick`.
 */
export function Sankey(props: {
  flows: Flows;
  center: string;
  unit: string;
  locale: string;
  label: string;
  restNote?: string;
  onPick?: (pointId: string) => void;
}) {
  const tot = () => props.flows.ins.reduce((s, f) => s + f.kw, 0);
  const totOut = () => props.flows.outs.reduce((s, f) => s + f.kw, 0);
  const base = () => Math.max(tot(), totOut()) || 1;
  const k = () => 210 / base();
  const slot = (v: number) => Math.max(v * k(), FS * 2.6);
  const height = () =>
    TOP +
    Math.max(
      props.flows.ins.reduce((s, f) => s + slot(f.kw) + GAP, 0),
      props.flows.outs.reduce((s, f) => s + slot(f.kw) + GAP, 0),
      base() * k(),
    ) +
    16;
  const fmt = (v: number) =>
    `${formatNumber(v, props.locale, 2)} ${props.unit}`;

  const placed = (flows: Flow[]) => {
    let y = TOP;
    let inner = TOP;
    return flows.map((f) => {
      const h = f.kw * k();
      const at = { f, y, inner, h };
      y += slot(f.kw) + GAP;
      inner += h;
      return at;
    });
  };
  const band = (x0: number, y0: number, x1: number, y1: number, h: number) =>
    `M${x0} ${y0} C ${(x0 + x1) / 2} ${y0}, ${(x0 + x1) / 2} ${y1}, ${x1} ${y1} L ${x1} ${y1 + h} C ${(x0 + x1) / 2} ${y1 + h}, ${(x0 + x1) / 2} ${y0 + h}, ${x0} ${y0 + h} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${height()}`}
      role="img"
      aria-label={props.label}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        "font-family": "var(--font-mono)",
        "font-size": `${FS}px`,
      }}
    >
      <defs>
        <pattern
          id="sankey-hatch"
          width="5"
          height="5"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="2" height="5" fill="var(--faint)" />
        </pattern>
      </defs>
      <rect
        x={CX}
        y={TOP}
        width="14"
        height={Math.max(1.5, tot() * k())}
        fill="var(--ink)"
      />
      <text x={CX + 7} y={TOP - 8} text-anchor="middle" fill="var(--ink)">
        {props.center} {fmt(props.flows.total)}
      </text>
      <For each={placed(props.flows.ins)}>
        {(p) => (
          <g>
            <rect
              x={XL}
              y={p.y}
              width="9"
              height={Math.max(1.5, p.h)}
              fill={COLORS[p.f.kind]}
            />
            <text
              x={XL - 6}
              y={p.y + p.h / 2 - 1}
              text-anchor="end"
              fill="var(--muted)"
            >
              {p.f.label}
            </text>
            <text
              x={XL - 6}
              y={p.y + p.h / 2 + FS}
              text-anchor="end"
              fill="var(--ink)"
            >
              {fmt(p.f.kw)}
            </text>
            <Show when={p.f.kw > 0}>
              <path
                d={band(XL + 9, p.y, CX, p.inner, p.h)}
                fill={COLORS[p.f.kind]}
                opacity=".33"
              />
            </Show>
          </g>
        )}
      </For>
      <For each={placed(props.flows.outs)}>
        {(p) => {
          const rest = p.f.kind === "rest";
          const pick = () => p.f.pointId && props.onPick?.(p.f.pointId);
          return (
            <g
              style={
                p.f.pointId && props.onPick ? { cursor: "pointer" } : undefined
              }
              onClick={pick}
            >
              <Show when={p.f.kw > 0}>
                <path
                  d={band(CX + 14, p.inner, XR, p.y, p.h)}
                  fill={COLORS[p.f.kind]}
                  opacity={rest ? 0.5 : 0.3}
                />
              </Show>
              <rect
                x={XR}
                y={p.y}
                width="9"
                height={Math.max(1.5, p.h)}
                fill={rest ? "url(#sankey-hatch)" : COLORS[p.f.kind]}
                stroke={rest ? "var(--faint)" : "none"}
              />
              <text x={XR + 15} y={p.y + p.h / 2 - 1} fill="var(--muted)">
                {p.f.label}
              </text>
              <text x={XR + 15} y={p.y + p.h / 2 + FS} fill="var(--ink)">
                {fmt(p.f.kw)}
                {rest && props.restNote ? ` · ${props.restNote}` : ""}
              </text>
            </g>
          );
        }}
      </For>
    </svg>
  );
}
